/**
 * Session Warning Modal - displays when session is about to expire.
 */
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useSession } from "../../contexts/SessionContext";
import { useAuth } from "../../contexts/AuthContext";

export const SessionWarningModal: React.FC = () => {
  const { showWarning, expiresAt, isRefreshing, extendSession, dismissWarning } = useSession();
  const { signOut } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<string>("");

  // Update time remaining every second when warning is shown
  useEffect(() => {
    if (!showWarning || !expiresAt) return;

    const updateTimeRemaining = () => {
      const remaining = expiresAt.getTime() - Date.now();
      if (remaining <= 0) {
        setTimeRemaining("0:00");
        return;
      }

      const minutes = Math.floor(remaining / 60000);
      const seconds = Math.floor((remaining % 60000) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, "0")}`);
    };

    updateTimeRemaining();
    const interval = setInterval(updateTimeRemaining, 1000);

    return () => clearInterval(interval);
  }, [showWarning, expiresAt]);

  const handleExtend = async () => {
    try {
      setError(null);
      await extendSession();
    } catch {
      setError("Failed to extend session. Please try again or sign in again.");
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {
      // Ignore sign out errors, will redirect anyway
    }
  };

  return (
    <Dialog
      open={showWarning}
      onClose={dismissWarning}
      maxWidth="xs"
      fullWidth
      aria-labelledby="session-dialog-title"
      aria-describedby="session-dialog-description"
    >
      <DialogTitle id="session-dialog-title">Session Expiring Soon</DialogTitle>
      <DialogContent>
        <Typography id="session-dialog-description" variant="body1" sx={{ mb: 2 }}>
          Your session will expire in <strong>{timeRemaining}</strong>.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Would you like to extend your session or sign out now?
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleSignOut} disabled={isRefreshing}>
          Sign Out
        </Button>
        <Button
          onClick={handleExtend}
          variant="contained"
          disabled={isRefreshing}
          startIcon={isRefreshing ? <CircularProgress size={16} /> : null}
        >
          {isRefreshing ? "Extending..." : "Extend Session"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
